import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, RefreshCw, Eye } from "lucide-react";

/** Phase 5 — Stock & Display Reports desktop (PM/Admin/Sales Head). */
export default function StockReportsManagement() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ from_date: "", to_date: "", brand: "", product_category: "", display_condition: "__all__" });
  const [view, setView] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "__all__") params[k] = v; });
      const { data } = await api.get("/promoter/stock-reports", { params });
      setRows(data || []);
    } catch (e) {
      toast.error("Could not load", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const exportXlsx = () => {
    const token = localStorage.getItem("access_token");
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "__all__") params.append(k, v); });
    fetch(`${API}/promoter/stock-reports/export.xlsx?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error("export"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `stock_reports.xlsx`; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Could not export"));
  };

  return (
    <div className="space-y-4" data-testid="stock-mgmt-page">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Stock &amp; Display</h1>
          <p className="text-sm text-slate-500 mt-1">Promoter-submitted stock and display checks across counters.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="stock-refresh-btn"><RefreshCw size={14} className="mr-1" /> Refresh</Button>
          <Button variant="outline" onClick={exportXlsx} data-testid="stock-export-btn"><Download size={14} className="mr-1" /> Export</Button>
        </div>
      </header>

      <Card><CardContent className="p-3 grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
        <Field label="From"><Input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} data-testid="stock-filter-from" /></Field>
        <Field label="To"><Input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} data-testid="stock-filter-to" /></Field>
        <Field label="Brand"><Input value={filters.brand} onChange={(e) => setFilters({ ...filters, brand: e.target.value })} placeholder="Any" data-testid="stock-filter-brand" /></Field>
        <Field label="Category"><Input value={filters.product_category} onChange={(e) => setFilters({ ...filters, product_category: e.target.value })} placeholder="Any" data-testid="stock-filter-category" /></Field>
        <Field label="Condition">
          <Select value={filters.display_condition} onValueChange={(v) => setFilters({ ...filters, display_condition: v })}>
            <SelectTrigger className="h-8" data-testid="stock-filter-condition"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All</SelectItem>
              <SelectItem value="good">Good</SelectItem>
              <SelectItem value="average">Average</SelectItem>
              <SelectItem value="poor">Poor</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Button onClick={load} className="h-8 bg-slate-900 hover:bg-slate-800 text-white" data-testid="stock-apply-btn">Apply</Button>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="stock-table">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Promoter</th>
                <th className="text-left px-3 py-2">Dealer / Area</th>
                <th className="text-left px-3 py-2">Brand / Cat / Model</th>
                <th className="text-right px-3 py-2">Stock</th>
                <th className="text-right px-3 py-2">Display</th>
                <th className="text-right px-3 py-2">Replen.</th>
                <th className="text-left px-3 py-2">Condition</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="text-center py-6 text-slate-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={9} className="text-center py-6 text-slate-400" data-testid="stock-empty">
                  No reports for this filter.
                </td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60"
                    data-testid={`stock-row-${r.id.slice(0, 8)}`}>
                  <td className="px-3 py-2 font-mono text-xs">{r.report_date}</td>
                  <td className="px-3 py-2">{r.promoter_name}<div className="text-[10px] text-slate-400">{r.promoter_promoter_id}</div></td>
                  <td className="px-3 py-2 text-slate-600">{r.dealer_name || "—"}<div className="text-[10px] text-slate-500">{r.area || ""}</div></td>
                  <td className="px-3 py-2">{r.brand} <span className="text-slate-500">/ {r.product_category || "—"} /</span> {r.product_model}</td>
                  <td className="px-3 py-2 text-right">{r.available_stock_qty}</td>
                  <td className="px-3 py-2 text-right">{r.display_qty}</td>
                  <td className="px-3 py-2 text-right">{r.required_replenishment_qty ?? "—"}</td>
                  <td className="px-3 py-2"><ConditionPill c={r.display_condition} /></td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => setView(r)}
                            data-testid={`stock-view-${r.id.slice(0, 8)}`}>
                      <Eye size={12} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-2xl" data-testid="stock-detail-dialog">
          {view && (
            <>
              <DialogHeader>
                <DialogTitle>{view.brand} {view.product_model} · {view.report_date}</DialogTitle>
                <DialogDescription>
                  Stock &amp; display snapshot submitted by the promoter from their assigned counter.
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Row label="Promoter" value={`${view.promoter_name} (${view.promoter_promoter_id})`} />
                <Row label="Dealer / Area" value={`${view.dealer_name || "—"} · ${view.area || "—"}`} />
                <Row label="Brand / Cat / Model" value={`${view.brand} / ${view.product_category || "—"} / ${view.product_model}`} />
                <Row label="Available Stock Qty" value={view.available_stock_qty} />
                <Row label="Display Qty" value={view.display_qty} />
                <Row label="Display Condition" value={view.display_condition} />
                <Row label="Required Replenishment" value={view.required_replenishment_qty ?? "—"} />
                <Row label="Competitor Remarks" value={view.competitor_display_remarks || "—"} />
                <Row label="Remarks" value={view.remarks || "—"} />
                <Row label="Submitted At" value={(view.created_at || "").slice(0, 19)} />
              </div>
              {view.photo_url && (
                <div className="mt-2">
                  <div className="text-xs font-semibold text-slate-600 mb-1">Photo</div>
                  <img src={view.photo_url} alt="display" className="max-h-96 w-auto rounded border" data-testid="stock-detail-photo" />
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase font-semibold text-slate-600">{label}</Label>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-slate-900 break-words">{String(value)}</div>
    </div>
  );
}

function ConditionPill({ c }) {
  const m = {
    good: "bg-emerald-100 text-emerald-800",
    average: "bg-amber-100 text-amber-800",
    poor: "bg-rose-100 text-rose-800",
  }[c] || "bg-slate-100 text-slate-700";
  return <Badge className={m}>{c}</Badge>;
}
